import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface QuestionResult {
  questionId: string;
  lessonId: string; // Adicionar lessonId para garantir unicidade
  correct: boolean;
  points: number;
  selectedAnswers: string[];
  correctCount?: number; // Número de respostas corretas selecionadas
  incorrectCount?: number; // Número de respostas incorretas selecionadas
  totalCorrect?: number; // Total de respostas corretas esperadas
}

export interface EvaluationResults {
  lessonId: string;
  totalScore: number;
  totalPoints: number;
  percentage: number;
  questionResults: QuestionResult[];
  submittedAt: Date;
}

export interface DraftAnswers {
  lessonId: string;
  answers: Map<string, string[]>; // questionId -> selected optionIds
}

@Injectable({
  providedIn: 'root',
})
export class EvaluationResultsService {
  // ==========================================================================
  // PROPRIEDADES
  // ==========================================================================

  private resultsSubject = new BehaviorSubject<EvaluationResults | null>(null);
  public results$: Observable<EvaluationResults | null> =
    this.resultsSubject.asObservable();

  // Armazena rascunhos por lição (usando Map para facilitar acesso)
  private draftsMap = new Map<string, Map<string, string[]>>(); // lessonId -> (questionId -> optionIds[])

  // ==========================================================================
  // MÉTODOS PÚBLICOS
  // ==========================================================================

  /**
   * Salva os resultados da avaliação
   */
  public saveResults(results: EvaluationResults): void {
    this.resultsSubject.next(results);
    // Persistir no localStorage
    this.persistResults(results);
  }

  /**
   * Obtém os resultados salvos
   */
  public getResults(): EvaluationResults | null {
    // Tentar carregar do localStorage se não houver em memória
    if (!this.resultsSubject.value) {
      const loaded = this.loadResults();
      if (loaded) {
        this.resultsSubject.next(loaded);
      }
    }
    return this.resultsSubject.value;
  }

  /**
   * Limpa os resultados salvos
   */
  public clearResults(): void {
    this.resultsSubject.next(null);
    // Limpar do localStorage também
    try {
      localStorage.removeItem('evaluation_results');
    } catch (error) {
      console.error('Erro ao limpar resultados do localStorage:', error);
    }
  }

  /**
   * Salva rascunho de respostas para uma lição específica
   */
  public saveDraft(
    lessonId: string,
    questionId: string,
    selectedOptionIds: string[],
  ): void {
    if (!this.draftsMap.has(lessonId)) {
      this.draftsMap.set(lessonId, new Map());
    }

    const lessonDrafts = this.draftsMap.get(lessonId)!;
    lessonDrafts.set(questionId, selectedOptionIds);

    // Persistir no localStorage
    this.persistDrafts();
  }

  /**
   * Obtém rascunho de respostas para uma lição específica
   */
  public getDraft(lessonId: string): Map<string, string[]> {
    const lessonDrafts = this.draftsMap.get(lessonId);
    if (lessonDrafts) {
      return new Map(lessonDrafts);
    }
    return new Map();
  }

  /**
   * Obtém rascunho de uma questão específica
   */
  public getQuestionDraft(lessonId: string, questionId: string): string[] {
    const lessonDrafts = this.draftsMap.get(lessonId);
    if (lessonDrafts) {
      return lessonDrafts.get(questionId) || [];
    }
    return [];
  }

  /**
   * Limpa rascunho de uma lição específica
   */
  public clearDraft(lessonId: string): void {
    this.draftsMap.delete(lessonId);
    this.persistDrafts();
  }

  /**
   * Limpa todos os rascunhos
   */
  public clearAllDrafts(): void {
    this.draftsMap.clear();
    this.persistDrafts();
  }

  /**
   * Persiste rascunhos no localStorage
   */
  private persistDrafts(): void {
    try {
      const draftsData: Record<string, Record<string, string[]>> = {};
      this.draftsMap.forEach((questionMap, lessonId) => {
        draftsData[lessonId] = Object.fromEntries(questionMap);
      });
      localStorage.setItem('evaluation_drafts', JSON.stringify(draftsData));
    } catch (error) {
      console.error('Erro ao salvar rascunhos:', error);
    }
  }

  /**
   * Carrega rascunhos do localStorage
   */
  private loadDrafts(): void {
    try {
      const draftsData = localStorage.getItem('evaluation_drafts');
      if (draftsData) {
        const parsed: Record<string, Record<string, string[]>> = JSON.parse(
          draftsData,
        );
        Object.entries(parsed).forEach(([lessonId, questionMap]) => {
          this.draftsMap.set(lessonId, new Map(Object.entries(questionMap)));
        });
      }
    } catch (error) {
      console.error('Erro ao carregar rascunhos:', error);
    }
  }

  /**
   * Persiste resultados no localStorage
   */
  private persistResults(results: EvaluationResults): void {
    try {
      // Converter Date para string para serialização JSON
      const resultsToSave = {
        ...results,
        submittedAt: results.submittedAt.toISOString(),
      };
      localStorage.setItem('evaluation_results', JSON.stringify(resultsToSave));
    } catch (error) {
      console.error('Erro ao salvar resultados:', error);
    }
  }

  /**
   * Carrega resultados do localStorage
   */
  private loadResults(): EvaluationResults | null {
    try {
      const resultsData = localStorage.getItem('evaluation_results');
      if (resultsData) {
        const parsed = JSON.parse(resultsData);
        // Converter string de data de volta para Date
        return {
          ...parsed,
          submittedAt: new Date(parsed.submittedAt),
        };
      }
    } catch (error) {
      console.error('Erro ao carregar resultados:', error);
    }
    return null;
  }

  constructor() {
    // Carregar rascunhos ao inicializar o serviço
    this.loadDrafts();
    // Carregar resultados salvos ao inicializar o serviço
    const loadedResults = this.loadResults();
    if (loadedResults) {
      this.resultsSubject.next(loadedResults);
    }
  }
}
